'use client';

import {
	FormControl,
	IconButton,
	InputAdornment,
	FormHelperText,
	Input,
	InputLabel,
	InputProps,
	Box,
} from '@mui/material';
import React, { useId, useState } from 'react';
import { Icon } from '../../blocks/icons';
import styles from './Password.module.scss';

type Password = {
	errorText?: string;
	showVisibilityToggle?: boolean;
	variant?: 'outlined' | 'standard';
	label?: string;
	labelEndAdornment?: React.ReactNode;
	dataCy?: string;
} & InputProps;

export const Password: React.FC<Password> = ( {
	className,
	errorText,
	showVisibilityToggle = true,
	variant = 'outlined',
	slotProps,
	inputProps,
	value,
	onChange,
	label = 'Password',
	labelEndAdornment,
	dataCy = 'password-field',
	...textFieldProps
} ) => {
	const [ showPassword, setShowPassword ] = useState( false );

	const handleMouseDownPassword = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.preventDefault();
	};

	const handleMouseUpPassword = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.preventDefault();
	};

	// so when we have multiple of these on a single page the Playwright getByLabel (and screen reader) stuff works
	const inputId = useId();

	return (
		<FormControl variant={ variant } className={ className }>
			{ labelEndAdornment ? (
				<Box className={ styles.labelRow }>
					<InputLabel htmlFor={ inputId }>{ label }</InputLabel>
					{ labelEndAdornment }
				</Box>
			) : (
				<InputLabel htmlFor={ inputId }>{ label }</InputLabel>
			) }
			<Input
				{ ...textFieldProps }
				id={ inputId }
				data-cy={ dataCy }
				type={ showPassword ? 'text' : 'password' }
				value={ value }
				onChange={ onChange }
				error={ !!errorText }
				placeholder='••••••••'
				endAdornment={
					<InputAdornment position='end'>
						<IconButton
							aria-label={
								showPassword ? 'hide the password' : 'display the password'
							}
							onClick={ () => setShowPassword( !showPassword ) }
							onMouseDown={ handleMouseDownPassword }
							onMouseUp={ handleMouseUpPassword }
							edge='end'
						>
							<Icon
								className={ styles.icon }
								type={ showPassword ? 'visible' : 'visibleOff' }
							/>
						</IconButton>
					</InputAdornment>
				}
			/>
			{ errorText && (
				<FormHelperText data-cy='password-validation' className={ styles.error }>
					{ errorText }
				</FormHelperText>
			) }
		</FormControl>
	);
};
